
import { Button } from "@/components/ui/button";
import { Search, X } from "lucide-react";
import { SearchFilter } from "./SearchFilter";
import { IndustryFilter } from "./IndustryFilter";
import { LocationFilter } from "./LocationFilter";
import { CompanySizeFilter } from "./CompanySizeFilter";
import { SkillFilter } from "./SkillFilter";
import { CompanyFilter } from "./CompanyFilter";
import { RoleFilter } from "./RoleFilter";
import { WorkArrangementFilter } from "./WorkArrangementFilter";
import { SalaryRangeFilter } from "./SalaryRangeFilter";
import { useMemo } from 'react';

interface FilterContainerProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  industryFilter: string;
  onIndustryChange: (value: string) => void;
  locationFilter: string;
  onLocationChange: (value: string) => void;
  sizeFilter: string;
  onSizeFilter: (value: string) => void;
  skillFilter: string;
  onSkillChange: (value: string) => void;
  companyFilter: string;
  onCompanyChange: (value: string) => void;
  roleFilter: string;
  onRoleChange: (value: string) => void;
  workArrangementFilter: string;
  onWorkArrangementChange: (value: string) => void;
  salaryRange: [number, number];
  onSalaryRangeChange: (value: [number, number]) => void;
  showMyJobs?: boolean;
  onShowMyJobsChange?: (value: boolean) => void;
  onApplyFilters: () => void;
  onResetFilters: () => void;
  uniqueLocations: string[];
  uniqueIndustries: string[];
  uniqueSkills: string[];
  uniqueCompanies: string[];
}

export function FilterContainer({
  searchQuery,
  onSearchChange,
  industryFilter,
  onIndustryChange,
  locationFilter,
  onLocationChange,
  sizeFilter,
  onSizeFilter,
  skillFilter,
  onSkillChange,
  companyFilter,
  onCompanyChange,
  roleFilter,
  onRoleChange,
  workArrangementFilter,
  onWorkArrangementChange,
  salaryRange,
  onSalaryRangeChange,
  onApplyFilters,
  onResetFilters,
  uniqueLocations,
  uniqueIndustries,
  uniqueSkills,
  uniqueCompanies,
}: FilterContainerProps) {
  // Memoize the filter components to prevent unnecessary re-renders
  const searchFilterComponent = useMemo(() => (
    <SearchFilter 
      searchQuery={searchQuery}
      onSearchChange={onSearchChange}
    />
  ), [searchQuery, onSearchChange]);

  const industryFilterComponent = useMemo(() => (
    <IndustryFilter
      industryFilter={industryFilter}
      onIndustryChange={onIndustryChange}
      industries={uniqueIndustries}
    />
  ), [industryFilter, onIndustryChange, uniqueIndustries]);

  const locationFilterComponent = useMemo(() => (
    <LocationFilter
      locationFilter={locationFilter}
      onLocationChange={onLocationChange}
      locations={uniqueLocations}
    />
  ), [locationFilter, onLocationChange, uniqueLocations]);

  const skillFilterComponent = useMemo(() => (
    <SkillFilter
      skillFilter={skillFilter}
      onSkillChange={onSkillChange}
      uniqueSkills={uniqueSkills}
    />
  ), [skillFilter, onSkillChange, uniqueSkills]);

  const companyFilterComponent = useMemo(() => (
    <CompanyFilter
      companyFilter={companyFilter}
      onCompanyChange={onCompanyChange}
      uniqueCompanies={uniqueCompanies}
    />
  ), [companyFilter, onCompanyChange, uniqueCompanies]);

  return (
    <div className="space-y-6 bg-card p-6 rounded border-border border-slate-200 border">
      <div className="space-y-4">
        <div className="space-y-4">
          {searchFilterComponent}
          
          {industryFilterComponent}

          {locationFilterComponent}

          <CompanySizeFilter
            value={sizeFilter}
            onChange={onSizeFilter}
          />

          {skillFilterComponent}

          {companyFilterComponent}

          <RoleFilter
            roleFilter={roleFilter}
            onRoleChange={onRoleChange}
          />

          <WorkArrangementFilter
            workArrangementFilter={workArrangementFilter}
            onWorkArrangementChange={onWorkArrangementChange}
          />

          <SalaryRangeFilter
            salaryRange={salaryRange}
            onSalaryRangeChange={onSalaryRangeChange}
          />
        </div>

        <div className="flex flex-col gap-2">
          <Button 
            onClick={onApplyFilters}
            className="w-full bg-secondary hover:bg-secondary-hover text-primary-foreground rounded"
          >
            <Search className="w-4 h-4 mr-2" />
            Apply Filters
          </Button>
          <Button 
            variant="outline" 
            onClick={onResetFilters}
            className="w-full border-border bg-primary text-primary-foreground border-slate-200 hover:bg-slate-100 rounded"
          >
            <X className="w-4 h-4 mr-2" />
            Reset
          </Button>
        </div>
      </div>
    </div>
  );
}
